import type { CustomHoliday } from "@/api/CustomHoliday";
import type { CustomHolidaysListResponse } from "@/api/CustomHolidaysListResponse";
import type { CustomHolidaysStoreRequest } from "@/api/CustomHolidaysStoreRequest";
import type { Holiday } from "@/api/Holiday";
import type { StoreResponse } from "@/api/StoreResponse";
import { trackGoogleAnalyticsEvent } from "@/analytics/googleAnalytics";
import apiClient from "./core/ApiClient";

const holidays = (
  year: number,
  country?: string,
  language: string = "DE",
  city?: string | null,
  scope?: "public"
): Promise<Holiday[]> => {
  const params = new URLSearchParams({ language });
  if (country !== undefined) params.set("country", country);
  if (city != null) params.set("city", city.trim());
  if (scope !== undefined) params.set("scope", scope);
  trackGoogleAnalyticsEvent("holiday_request", {
    holiday_country: country?.trim().toUpperCase().slice(0, 100) || "(default)",
    holiday_city: city == null ? "(default)" : city.trim().replace(/\s+/g, " ").toLowerCase().slice(0, 100) || "(country-wide)",
  });
  return apiClient.get<Holiday[]>(`/api/holidays/public/${year}?${params}`);
};

const listCustom = (year: number): Promise<CustomHoliday[]> =>
  apiClient
    .get<CustomHolidaysListResponse>(`/api/holidays/custom?year=${year}`)
    .then((r) => [...r.results]);

const createCustom = (entry: CustomHoliday): Promise<number> =>
  apiClient
    .post<CustomHolidaysStoreRequest, StoreResponse>("/api/holidays/custom", { entry })
    .then((r) => r.id);

const updateCustom = (entry: CustomHoliday): Promise<number> =>
  apiClient
    .put<CustomHolidaysStoreRequest, StoreResponse>(`/api/holidays/custom/${entry.id}`, { entry })
    .then((r) => r.id);

const removeCustom = (id: number, effectiveDate?: string): Promise<void> =>
  apiClient.delete<void>(
    `/api/holidays/custom/${id}${effectiveDate === undefined ? "" : `?${new URLSearchParams({ effectiveDate })}`}`
  );

export const holidayService = { holidays, listCustom, createCustom, updateCustom, removeCustom };
